import { Loading, Panel } from "@appkit4/react-components";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import ConsolidatedPartnerships from "../../components/ConsolidatedPartnerships/ConsolidatedPartnerships";
import {
  deriveConsolidatedPartnershipElection,
  normalizeConsolidatedPartnerships,
  validateConsolidatedPartnerships,
} from "../../components/ConsolidatedPartnerships/consolidatedPartnershipUtils";
import CustomTooltipIcon from "../../components/customTooltipIcon/customTooltipIcon";
import toast from "../../components/DismissibleToast";
import { GroupNameInput } from "../../components/inputs/GroupNameInput";
import { BackButton, NextButton } from "../../components/ProgressNav";
import {
  Button,
  CalendarPicker,
  Input,
  InputNumber,
  Select,
  Switch,
} from "../../components/ReadonlyAwareInputs";
import CustomTableHeader from "../../components/tableHeader/tableHeader";
import { AuthContext } from "../../services/AuthProvider";
import {
  getGroupData,
  getGroupName,
  getPageTooltips,
  setGroupData,
} from "../../services/GroupContext";
import sqlService from "../../services/sqldatabase/sqldatabase.service";
import countries from "../../utils/Countries";
import {
  getDefaultPeriodEnd,
  getMaxPeriodEnd,
  getOriginatingPoaMinStartDateJS,
  processDate,
  processDateJS,
} from "../../utils/dateProcessor";
import { migrateUlt_parent_incorporation_jurisdiction } from "../../utils/migrationScripts";
import { sortByStartDate } from "../../utils/PeriodUtils";
import "./groupConfig.scss";
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);

const addInterestAllowanceToolTipTitle =
  "Click to add new Unused Interest Allowance";
const positiveNumErrorMessage = "Number must be positive";

const GroupConfig = () => {
  const randomUuid = uuidv4();

  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const auth = useContext(AuthContext);
  const tenantId = useSelector((state) => state.auth?.tenantId);

  const [groupName, setGroupName] = useState("");
  const [groupNameError, setGroupNameError] = useState(false);
  const [lastInvalidName, setLastInvalidName] = useState(null);
  const [invalidGroupNames, setInvalidGroupNames] = useState([]);
  const [invalidGroupName, setInvalidGroupName] = useState(false);
  useEffect(() => {
    setInvalidGroupName(invalidGroupNames.includes(groupName));
  }, [groupName, lastInvalidName]);

  const [companies, setCompanies] = useState("");
  const [ultParentName, setUltParentName] = useState("");
  const [ultParentJurisdiction, setUltParentJurisdiction] = useState("");
  const [ultParentLEI, setUltParentLEI] = useState("");
  const [firstPoAStart, setFirstPoAStart] = useState();
  const [firstPoAEnd, setFirstPoAEnd] = useState();
  const [maxPeriodEnd, setMaxPeriodEnd] = useState();
  const maxElectionDate = processDateJS(firstPoAStart)?.subtract(1, "day");

  const [originatingPoAStartMaxDate, setOriginatingPoAStartMaxDate] =
    useState();
  const [originatingPoAEndDates, setOriginatingPoAEndDates] = useState([]);

  // Siva: Long variables but I would rather that than non-descripitive names
  const [disregard_regulations_election, set_disregard_regulations_election] =
    useState(false);
  const [
    disregard_regulations_election_year,
    set_disregard_regulations_election_year,
  ] = useState(undefined);
  const [disregard_regulation_comment, set_disregard_regulation_comment] =
    useState();

  const [group_ebitda_gains, set_group_ebitda_gains] = useState(false);
  const [group_ebitda_gains_year, set_group_ebitda_gains_year] =
    useState(undefined);
  const [group_ebitda_gains_comment, set_group_ebitda_gains_comment] =
    useState();

  const [interest_allowance_alt_calc, set_interest_allowance_alt_calc] =
    useState(false);
  const [
    interest_allowance_alt_calc_year,
    set_interest_allowance_alt_calc_year,
  ] = useState(undefined);
  const [
    interest_allowance_alt_calc_comment,
    set_interest_allowance_alt_calc_comment,
  ] = useState();

  const [
    interest_allowance_consolidated_partnership,
    set_interest_allowance_consolidated_partnership,
  ] = useState(false);
  const [
    interest_allowance_consolidated_partnership_year,
    set_interest_allowance_consolidated_partnership_year,
  ] = useState(undefined);
  const [
    interest_allowance_consolidated_partnership_comment,
    set_interest_allowance_consolidated_partnership_comment,
  ] = useState();

  // Each row: { name: string, date: Date|null }
  const [duplicateCPErrors, setDuplicateCPErrors] = useState([]);
  const [consolidatedPartnershipsError, setConsolidatedPartnershipsError] =
    useState(false);
  const [
    consolidatedPartnershipsStartDateError,
    setConsolidatedPartnershipsStartDateError,
  ] = useState(false);

  const [excess_debt_cap_currency, set_excess_debt_cap_currency] = useState("");
  const [excess_debt_cap_before_model, set_excess_debt_cap_before_model] =
    useState(0);

  const [isError, setIsError] = useState(false);

  const [isFirstPoaEndDateValid, setIsFirstPoaEndDateValid] = useState(true);

  const [isUnusedInterestStartDatesValid, setIsUnusedInterestStartDatesValid] =
    useState();
  const [isUnusedInterestEndDatesValid, setIsUnusedInterestEndDatesValid] =
    useState();
  const [isUnusedInterestRowValid, setIsUnusedInterestRowValid] = useState();

  const [unusedInterest, setUnusedInterest] = useState();

  const [ultParentSearch, setUltParentSearch] = useState("");

  const [calendarPickerWidth, setCalendarPickerWidth] = useState(150);

  const [electionsDatesValid, setElectionsDatesValid] = useState({
    disregard_regulations_election_date: true,
    group_ebitda_gains_date: true,
    interest_allowance_alt_calc_date: true,
    interest_allowance_consolidated_partnership_date: true,
  });

  const tooltips = useMemo(() => getPageTooltips("groupConfig"), []);

  const disregardRegulationsElectionError =
    disregard_regulations_election &&
    (!disregard_regulations_election_year ||
      !electionsDatesValid.disregard_regulations_election_date);

  const groupEbitdaGainsError =
    group_ebitda_gains &&
    (!group_ebitda_gains_year || !electionsDatesValid.group_ebitda_gains_date);

  const interestAllowanceAltCalcError =
    interest_allowance_alt_calc &&
    (!interest_allowance_alt_calc_year ||
      !electionsDatesValid.interest_allowance_alt_calc_date);

  const newUltimateParentsIDs = useRef([]);

  const getConsolidatedPartnershipValidation = (
    partnerships = getGroupData()?.consolidated_partnerships || [],
    requireAtLeastOne = true,
  ) => {
    const poaPeriodEndDates =
      getGroupData()
        ?.periods_of_account?.map((poa) => poa?.period_end)
        ?.filter((date) => !!date) || [];

    return validateConsolidatedPartnerships({
      partnerships,
      poaPeriodEndDates,
      firstModelPeriodStartDate: getGroupData()?.model_first_period_start,
      requireAtLeastOne,
      requireUniqueNames: true,
    });
  };

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window?.innerWidth;
      if (windowWidth <= 1160) {
        setCalendarPickerWidth(100);
      } else if (windowWidth <= 1200) {
        setCalendarPickerWidth(120);
      } else if (windowWidth <= 1250) {
        setCalendarPickerWidth(130);
      } else if (windowWidth <= 1520) {
        setCalendarPickerWidth(150);
      } else {
        setCalendarPickerWidth(200);
      }
    };

    window?.addEventListener("resize", handleResize);

    return () => {
      window?.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsUnusedInterestRowValid(
      isUnusedInterestStartDatesValid?.map(
        (isValid, index) => isValid && isUnusedInterestEndDatesValid[index],
      ),
    );
  }, [isUnusedInterestStartDatesValid, isUnusedInterestEndDatesValid]);

  useEffect(() => {
    if (interest_allowance_consolidated_partnership) {
      const validation = getConsolidatedPartnershipValidation();
      setDuplicateCPErrors(validation.duplicateErrors);
      setConsolidatedPartnershipsError(validation.hasErrors);
      setConsolidatedPartnershipsStartDateError(validation.hasInvalidStartDate);
    } else {
      setDuplicateCPErrors([]);
      setConsolidatedPartnershipsError(false);
      setConsolidatedPartnershipsStartDateError(false);
    }
  }, [interest_allowance_consolidated_partnership, firstPoAStart, firstPoAEnd]);

  useEffect(() => {
    setMaxPeriodEnd(getMaxPeriodEnd(firstPoAStart));

    const originatingPoAEndDates = [];
    for (let index in unusedInterest) {
      originatingPoAEndDates.push({
        minDate: calculate_min_date(index),
        maxDate: calculate_max_date(index),
      });
    }

    setOriginatingPoAEndDates(originatingPoAEndDates);
    setOriginatingPoAStartMaxDate(
      processDateJS(firstPoAStart)?.subtract(1, "day"),
    );
  }, [firstPoAStart]);

  const consolidatedPartnerships =
    getGroupData()?.consolidated_partnerships || [];
  const hasPriorConsolidatedPartnership = normalizeConsolidatedPartnerships(
    consolidatedPartnerships,
  ).some((partnership) => {
    if (!partnership?.date || !firstPoAEnd) return false;

    const partnershipDate = processDateJS(partnership.date);
    const modelPeriodEnd = processDateJS(firstPoAEnd);

    return (
      partnershipDate &&
      modelPeriodEnd &&
      partnershipDate.isSameOrBefore(modelPeriodEnd, "day")
    );
  });

  const isConsolidatedPartnershipElectionTurnedOn =
    interest_allowance_consolidated_partnership ||
    hasPriorConsolidatedPartnership;

  const isSaveDisabled =
    isError ||
    groupNameError ||
    invalidGroupName ||
    isUnusedInterestEndDatesValid?.includes(false) ||
    isUnusedInterestStartDatesValid?.includes(false) ||
    !isFirstPoaEndDateValid ||
    duplicateCPErrors?.some((value) => value === true) ||
    !Object.values(electionsDatesValid)?.every((value) => value) ||
    disregardRegulationsElectionError ||
    groupEbitdaGainsError ||
    interestAllowanceAltCalcError ||
    consolidatedPartnershipsError ||
    consolidatedPartnershipsStartDateError;

  useEffect(() => {
    (async () => {
      const savedData =
        migrateUlt_parent_incorporation_jurisdiction(getGroupData());
      // Apply migration
      setGroupData(savedData);

      if (!savedData) {
        return;
      }

      setGroupName(getGroupName());
      setCompanies(savedData?.companies || {});
      setUltParentJurisdiction(
        savedData?.ult_parent_incorporation_jurisdiction || "",
      );
      setUltParentLEI(savedData?.ult_parent_lei || "");
      setFirstPoAStart(
        new Date(savedData?.model_first_period_start) || undefined,
      );
      setFirstPoAEnd(new Date(savedData?.model_first_period_end) || undefined);

      var companies = savedData?.companies;

      // If companies is still null, maybe it errored out above?
      if (!companies) {
        companies = [];
      }

      setCompanies(companies);
      const ultParent = companies?.find(
        (comp) => comp?.company_ID === savedData?.ult_parent_ID,
      );
      setUltParentName(ultParent ? ultParent?.company_name : "");

      let unusedInterest = savedData?.unused_interest;
      if (!unusedInterest || unusedInterest?.length === 0) {
        unusedInterest = [];
      } else {
        unusedInterest = unusedInterest?.map((r) => {
          return {
            ...r,
            start_date: new Date(r?.start_date),
            end_date: new Date(r?.end_date),
          };
        });
      }

      unusedInterest = sortByStartDate(unusedInterest);

      const get_date_or_undefined = (saved_data, key) => {
        const value = saved_data[key];
        return value ? new Date(value) : undefined;
      };

      set_disregard_regulations_election(
        savedData?.hasOwnProperty("disregard_regulations_election")
          ? savedData?.disregard_regulations_election
          : savedData?.disregard_regulations_to_derivative_contracts,
      );
      set_disregard_regulations_election_year(
        get_date_or_undefined(savedData, "disregard_regulations_election_year"),
      );
      set_disregard_regulation_comment(savedData?.disregard_regulation_comment);

      set_group_ebitda_gains(savedData?.group_ebitda_gains);
      set_group_ebitda_gains_year(
        get_date_or_undefined(savedData, "group_ebitda_gains_year"),
      );
      set_group_ebitda_gains_comment(savedData?.group_ebitda_gains_comment);

      set_interest_allowance_alt_calc(savedData?.interest_allowance_alt_calc);
      set_interest_allowance_alt_calc_year(
        get_date_or_undefined(savedData, "interest_allowance_alt_calc_year"),
      );
      set_interest_allowance_alt_calc_comment(
        savedData?.interest_allowance_alt_calc_comment,
      );

      set_interest_allowance_consolidated_partnership(
        savedData?.interest_allowance_consolidated_partnership,
      );
      const rawElectionYear = get_date_or_undefined(
        savedData,
        "interest_allowance_consolidated_partnership_year",
      );
      const processedElectionYear = rawElectionYear
        ? processDate(rawElectionYear)
        : undefined;
      set_interest_allowance_consolidated_partnership_year(
        processedElectionYear,
      );
      set_interest_allowance_consolidated_partnership_comment(
        savedData?.interest_allowance_consolidated_partnership_comment,
      );
      // Normalize consolidated partnerships - handle all old data formats
      // Process election date through processDate to avoid timezone issues
      const rawElectionDate = get_date_or_undefined(
        savedData,
        "interest_allowance_consolidated_partnership_year",
      );
      const electionDate = rawElectionDate
        ? processDate(rawElectionDate)
        : null;
      if (
        savedData?.hasOwnProperty("consolidated_partnerships") &&
        savedData?.consolidated_partnerships?.length > 0
      ) {
        const normalizedCP = (savedData?.consolidated_partnerships || [])
          .map((item) => {
            // Handle old format: array of strings
            if (typeof item === "string") {
              return { name: item.trim(), date: electionDate };
            }
            // Handle format: objects with name and date (or poa_date)
            if (item && typeof item === "object" && "name" in item) {
              // Check for 'date' field first, then 'poa_date' (old field name)
              const existingDate = item.date || item.poa_date;
              return {
                name:
                  typeof item.name === "string"
                    ? item.name
                    : String(item.name ?? ""),
                date: existingDate ? processDate(existingDate) : electionDate,
              };
            }
            // Unrecognized format - skip
            return null;
          })
          .filter((entry) => entry !== null);

        // Store normalized partnerships in groupData
        const updatedGroupData = {
          ...savedData,
          consolidated_partnerships:
            normalizedCP.length > 0 ? normalizedCP : [],
        };
        setGroupData(updatedGroupData);
      }

      set_excess_debt_cap_currency("GBP"); //(savedData.excess_debt_cap_currency)
      set_excess_debt_cap_before_model(
        savedData?.excess_debt_capt_before_model != null
          ? savedData?.excess_debt_capt_before_model
          : 0,
      );
      setUnusedInterest(unusedInterest);
      const validatorList = unusedInterest?.map((i) => true);
      setIsUnusedInterestStartDatesValid(validatorList);
      setIsUnusedInterestEndDatesValid(validatorList);

      setIsLoading(false);
    })();
  }, []);

  const setUltimateParentName = (value) => {
    resetUltimateParent();

    const companySection = [...companies];
    // find or generate an entry for the company
    const foundCompany = companySection?.find(
      (company) => company?.company_name === value,
    );
    if (!foundCompany) {
      const newUltimateParentID = randomUuid;

      companySection?.push({
        company_name: value,
        company_ID: newUltimateParentID,
      });
      newUltimateParentsIDs.current.push(newUltimateParentID);
    }
    setCompanies(companySection);
    setUltParentName(value);
  };

  const resetUltimateParent = () => {
    setUltParentName("");
    setUltParentJurisdiction("");
    setUltParentLEI("");
  };

  const save = async () => {
    if (isError) {
      throw "There is an error. Please fix before proceeding...";
    }

    if (auth.isReadOnlyUser()) {
      return;
    }

    setIsPosting(true);

    const groupData = getGroupData();

    let proceedToSave = true;

    if (
      ![groupData.group_name, groupData.group_display_name].includes(groupName)
    ) {
      try {
        await toast.promise(sqlService?.getGroupList(tenantId), {
          loading: "checking if new group name is valid...",
          success: (result) => {
            const responses = result?.data?.flatMap((response) => [
              response?.id?.trim()?.toLowerCase(),
              response?.name?.trim()?.toLowerCase(),
            ]);
            if (responses?.includes(groupName?.trim()?.toLowerCase())) {
              toast.error(`This group name already exists in the CIR app.`);
              setLastInvalidName(groupName);
              setIsPosting(false);
              proceedToSave = false;
            } else {
              return "Group name is valid.";
            }
          },
          error: (err) => {
            proceedToSave = false;
            return "Error checking group name.";
          },
        });
      } catch (error) {
        proceedToSave = false;
        toast.error(`Error: ${error?.message}`);
        setIsPosting(false);
        return;
      }
    }

    if (!proceedToSave) return;

    let periods_of_account = [...groupData.periods_of_account];
    if (newUltimateParentsIDs.current.length > 0) {
      const firstPoAUkCompanyIDs = periods_of_account[0]?.ukCompanyIDs ?? [];
      periods_of_account[0].ukCompanyIDs = [
        ...firstPoAUkCompanyIDs,
        ...newUltimateParentsIDs.current,
      ];
    }

    const ultParent = companies?.find(
      (comp) => comp?.company_name === ultParentName,
    );
    const ultParentID = ultParent ? ultParent?.company_ID : "";

    const partnerships = getGroupData()?.consolidated_partnerships || [];
    const consolidatedPartnershipValidation =
      getConsolidatedPartnershipValidation(partnerships, false);

    if (consolidatedPartnershipValidation.hasInvalidStartDate) {
      setConsolidatedPartnershipsError(true);
      setConsolidatedPartnershipsStartDateError(true);
      setIsPosting(false);
      toast.error(
        "Invalid consolidated partnership start date. Use a PoA period end date or a date before the first model PoA start date.",
      );
      return;
    }

    if (consolidatedPartnershipValidation.hasDuplicateNames) {
      setConsolidatedPartnershipsError(true);
      setConsolidatedPartnershipsStartDateError(false);
      setIsPosting(false);
      toast.error("Duplicate consolidated partnership names are not allowed.");
      return;
    }

    const { switchValue: autoCalculatedSwitch, yearValue: autoCalculatedYear } =
      deriveConsolidatedPartnershipElection(partnerships, firstPoAEnd);

    const newJson = {
      ...groupData,
      ...(groupData.group_name !== groupName
        ? { group_display_name: groupName }
        : {}),
      periods_of_account,
      ult_parent_ID: ultParentID,
      ult_parent_incorporation_jurisdiction: ultParentJurisdiction,
      ult_parent_lei: ultParentLEI,
      model_first_period_start: firstPoAStart,
      model_first_period_end: firstPoAEnd,
      excess_debt_cap_currency: excess_debt_cap_currency,
      excess_debt_capt_before_model: excess_debt_cap_before_model,
      companies: companies,
      disregard_regulations_election: disregard_regulations_election,
      disregard_regulations_election_year: disregard_regulations_election_year,
      disregard_regulation_comment: disregard_regulation_comment,
      group_ebitda_gains: group_ebitda_gains,
      group_ebitda_gains_year: group_ebitda_gains_year,
      group_ebitda_gains_comment: group_ebitda_gains_comment,
      interest_allowance_alt_calc: interest_allowance_alt_calc,
      interest_allowance_alt_calc_year: interest_allowance_alt_calc_year,
      interest_allowance_alt_calc_comment: interest_allowance_alt_calc_comment,
      interest_allowance_consolidated_partnership: autoCalculatedSwitch,
      interest_allowance_consolidated_partnership_year: autoCalculatedYear,
      interest_allowance_consolidated_partnership_comment:
        interest_allowance_consolidated_partnership_comment,
      consolidated_partnerships:
        consolidatedPartnershipValidation.normalizedPartnerships,
      unused_interest: unusedInterest,
      status_groupconfig_completed: true,
    };

    const result = sqlService?.postResponse(newJson);
    const onSuccess = () => {
      setIsPosting(false);
      setGroupData(newJson);
    };
    const onError = () => {
      setIsPosting(false);
    };
    return toast.save(result, onSuccess, onError);
  };

  const is_unusued_interest_valid = (row) => {
    const fieldsToValidate = [
      "interest_allowance",
      "interest_expense",
      "reactivations",
      "reactivations_prior_model_use",
    ];

    for (let key of fieldsToValidate) {
      if (isNumberFractional(row[key]) || row[key] < 0) return false;
    }

    return true;
  };

  // A helper method to ensure that the data that is currently contained in unused interest is valid
  const isUnusuedInterestValid = () => {
    if (!unusedInterest || unusedInterest?.length === 0) {
      return true;
    }

    // Check the individual rows are valid
    for (const index in unusedInterest) {
      const row = unusedInterest[index];

      const startDate = processDateJS(row?.start_date);
      if (!startDate) {
        return false;
      }

      const endDate = processDateJS(row?.end_date);
      if (!endDate) {
        return false;
      }

      if (endDate?.isBefore(startDate)) {
        return false;
      }

      if (endDate?.isAfter(getMaxPeriodEnd(startDate))) {
        return false;
      }

      if (!is_unusued_interest_valid(row)) {
        return false;
      }

      if (!is_reactivations_valid(row)) {
        return false;
      }

      if (!is_reactivations_prior_model_valid(row)) {
        return false;
      }
    }

    if (is_unusued_interest_dates_valid()) {
      return false;
    }

    return true;
  };

  const is_reactivations_valid = (row) => {
    if (!row) {
      return true;
    }

    const max_reactivations = Math.max(
      row?.interest_allowance - row?.interest_expense,
      0,
    );
    if (row?.reactivations > max_reactivations) {
      return false;
    }

    return true;
  };

  const is_reactivations_prior_model_valid = (row) => {
    if (!row) {
      return true;
    }

    const max_reactivations_prior_model = Math.max(
      row?.interest_allowance - row?.interest_expense - row?.reactivations,
      0,
    );
    if (row?.reactivations_prior_model_use > max_reactivations_prior_model) {
      return false;
    }

    return true;
  };

  const is_unusued_interest_dates_valid = () => {
    const dates = unusedInterest?.map((obj) => ({
      start_date: obj?.start_date ? processDateJS(obj?.start_date) : undefined,
      end_date: obj?.end_date ? processDateJS(obj?.end_date) : undefined,
    }));

    for (let i = 0; i < dates?.length; i++) {
      const current_row = dates[i];

      for (let j = i + 1; j < dates?.length; j++) {
        const next_row = dates[j];

        if (
          current_row?.start_date &&
          next_row?.start_date &&
          current_row?.start_date?.isBefore(next_row?.end_date) &&
          next_row?.start_date?.isBefore(current_row?.end_date)
        ) {
          return true;
        }
      }
    }
    return false;
  };

  // update error values when fields with validation change
  useEffect(() => {
    var errorFlag = false;
    if (!ultParentName) {
      errorFlag = true;
    } else if (ultParentLEI && ultParentLEI?.length < 20) {
      errorFlag = true;
    } else if (ultParentJurisdiction?.length === 0) {
      errorFlag = true;
    } else if (!isUnusuedInterestValid()) {
      errorFlag = true;
    } else if (!firstPoAStart) {
      errorFlag = true;
    } else if (!firstPoAEnd) {
      errorFlag = true;
    } else if (excess_debt_cap_before_model) {
      if (!Number.isInteger(excess_debt_cap_before_model)) {
        errorFlag = true;
      }
    }
    setIsError(errorFlag);
  }, [
    ultParentLEI,
    ultParentJurisdiction,
    unusedInterest,
    firstPoAStart,
    firstPoAEnd,
    excess_debt_cap_before_model,
    excess_debt_cap_currency,
  ]);

  const tableChange = (index, value, name) => {
    if (name === "start_date" || name === "end_date") {
      value = processDate(value);
    }
    const rowsInput = [...unusedInterest];
    rowsInput[index][name] = value;
    rowsInput[index]["unused"] = Math.max(
      rowsInput[index]["interest_allowance"] -
        rowsInput[index]["interest_expense"] -
        rowsInput[index]["reactivations"],
      0,
    );
    rowsInput[index]["unused_available"] =
      rowsInput[index]["unused"] -
      rowsInput[index]["reactivations_prior_model_use"];

    if (name === "start_date" && rowsInput[index]["start_date"]) {
      rowsInput[index]["end_date"] = getDefaultPeriodEnd(
        rowsInput[index]["start_date"],
      );
    }
    const sortedList = sortByStartDate(rowsInput);
    setUnusedInterest(sortedList);
  };

  const addTableRows = () => {
    let new_period_start = undefined;
    let new_period_end = undefined;
    if (unusedInterest?.length > 0) {
      const previousData = sortByStartDate(unusedInterest);
      const lastPoA = previousData[previousData?.length - 1];
      new_period_start = processDateJS(lastPoA?.end_date)?.add(1, "day");
      new_period_end = getDefaultPeriodEnd(new_period_start)?.toDate();
      new_period_start = processDate(new_period_start);
    }
    let validatorList = [...isUnusedInterestStartDatesValid, true];
    setIsUnusedInterestStartDatesValid(validatorList);

    const newUnusedInterestList = [
      ...unusedInterest,
      {
        start_date: new_period_start,
        end_date: new_period_end,
        interest_allowance: "",
        interest_expense: "",
        reactivations: "",
        unused: "",
        reactivations_prior_model_use: "",
        unused_available: "",
      },
    ];

    const isNewEndDateValid =
      processDateJS(new_period_end)?.isSameOrBefore(
        calculate_max_date_from_list(
          newUnusedInterestList?.length - 1,
          newUnusedInterestList,
        ),
      ) ?? true;
    validatorList = [...isUnusedInterestEndDatesValid, isNewEndDateValid];
    setIsUnusedInterestEndDatesValid(validatorList);

    setUnusedInterest(newUnusedInterestList);
  };

  const deleteTableRows = (e, index) => {
    const rows = [...unusedInterest];
    rows?.splice(index, 1);
    setUnusedInterest(rows);

    let validatorList = [...isUnusedInterestStartDatesValid];
    validatorList?.splice(index, 1);
    setIsUnusedInterestStartDatesValid(validatorList);

    validatorList = [...isUnusedInterestEndDatesValid];
    validatorList?.splice(index, 1);
    setIsUnusedInterestEndDatesValid(validatorList);
  };

  const calculate_min_date = (index) => {
    const row = unusedInterest[index];
    if (!row?.start_date) {
      return getOriginatingPoaMinStartDateJS();
    }
    const min_date = processDateJS(row?.start_date);
    return min_date?.toDate();
  };

  const calculate_max_date = (index) => {
    return calculate_max_date_from_list(index, unusedInterest);
  };

  const calculate_max_date_from_list = (index, list) => {
    const row = list[index];
    return (
      dayjs.min(
        getMaxPeriodEnd(row?.start_date),
        processDateJS(firstPoAStart)?.subtract(1, "day"),
      ) ?? null
    );
  };

  // functions for handling consolidated partnership table
  const isNumberFractional = (num) => {
    return num !== "" && !Number.isInteger(num);
  };

  if (isLoading) {
    return (
      <Loading
        loadingType="linear"
        indeterminate={true}
        compact={false}
        className="page-loader"
      ></Loading>
    );
  }

  const getInputNumberErrorMessage = (key, num, max) => {
    if (num > max) return `${key} must be bigger than ${max}`;
    else if (num < 0) return positiveNumErrorMessage;
    else if (isNumberFractional(num))
      return "Entry value needs to be an integer";
  };

  const getInputNumber = (data, index, key, max = undefined) => {
    return (
      <InputNumber
        style={{ minWidth: calendarPickerWidth - 50 }}
        format=","
        className="col"
        value={data[key] || 0}
        error={data[key] < 0 || isNumberFractional(data[key])}
        errorNode={getInputNumberErrorMessage(key, data[key], max)}
        max={max}
        onChange={(evnt) => {
          tableChange(index, evnt, key);
        }}
      />
    );
  };

  return (
    <div className="ap-container">
      <Panel title="Initial Group Information" className="mt-4">
        <div className="row">
          <p className="col-4 flex-align">
            Group Name <span style={{ color: "red" }}>*</span>
            <CustomTooltipIcon size="big" tooltipText={tooltips.groupName} />
          </p>

          <GroupNameInput
            className="col"
            title={"Group Name"}
            hideTitleOnInput
            value={groupName}
            onChange={setGroupName}
            setError={setGroupNameError}
            error={groupNameError || invalidGroupName}
            errorNode={
              invalidGroupName
                ? "This name is already associated with a group"
                : ""
            }
            enabledInGroupConfigWhenFirstPeriodFinalized
          />
        </div>

        <div className="row">
          <p className="col-4 flex-align">
            Name of Ultimate Parent <span style={{ color: "red" }}>*</span>
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.ultimateParentName}
            />
          </p>
          <Select
            className="col"
            placeholder="Name of Ultimate Parent"
            hideTitleOnInput
            searchable
            onSearch={(val) => setUltParentSearch(val)}
            data={companies?.map((c) => c?.company_name)}
            onSelect={(value) => setUltimateParentName(value)}
            value={ultParentName}
            error={!ultParentName}
            defaultValue={ultParentName}
            required={true}
            noResultFound={
              <span>
                {ultParentSearch?.length === 0 ? (
                  <>
                    <button>No companies found, please enter a value</button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setUltimateParentName(ultParentSearch);
                      }}
                    >
                      Add new company: '{ultParentSearch}'
                    </button>
                  </>
                )}
              </span>
            }
          />
        </div>
        <div className="row">
          <p className="col-4 flex-align">
            Jurisdiction of Incorporation of Ultimate Parent:{" "}
            <span style={{ color: "red" }}>*</span>
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.jurisdictionOfIncorporation}
            />
          </p>
          <Select
            className="col"
            searchable
            data={countries}
            readonly
            value={ultParentJurisdiction}
            onSelect={(value) => setUltParentJurisdiction(value)}
            placeholder="Select Jurisdiction"
            error={!ultParentJurisdiction}
            hideTitleOnInput
            required={true}
          ></Select>
        </div>
        <div className="row">
          <p className="col-4 flex-align">
            LEI of Ultimate Parent (if available):
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.LEI}
              delay={6}
            />
          </p>
          <Input
            className="col"
            value={ultParentLEI}
            onChange={(value) => {
              if (value && !/^\w{1,20}$/.test(value)) return;
              setUltParentLEI(value);
            }}
            enabledInGroupConfigWhenFirstPeriodFinalized
            error={Boolean(ultParentLEI && ultParentLEI?.length < 20)}
            placeholder={"LEI of Ultimate Parent "}
            hideTitleOnInput
          ></Input>
        </div>
        <div className="row">
          <p className="col-4 flex-align">
            First Period of Account for which the Model is used (Start):{" "}
            <span style={{ color: "red" }}>*</span>
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.firstPoAStart}
            />
          </p>
          <CalendarPicker
            className="col"
            fieldTitle="First PoA Start"
            hideTitleOnInput
            value={firstPoAStart}
            format="DD/MM/YYYY"
            placeholder="dd/mm/yyyy"
            minDate={getOriginatingPoaMinStartDateJS()}
            onChange={(value) => {
              setFirstPoAStart(processDate(value));
              setFirstPoAEnd(getDefaultPeriodEnd(value)?.toDate());
            }}
            required={true}
          />
        </div>
        <div className="row">
          <p className="col-4 flex-align">
            First Period of Account for which the Model is used (End):{" "}
            <span style={{ color: "red" }}>*</span>
            <CustomTooltipIcon size="big" tooltipText={tooltips.firstPoAEnd} />
          </p>
          <CalendarPicker
            title="group_name"
            className="col"
            fieldTitle="First PoA End"
            hideTitleOnInput
            value={firstPoAEnd}
            format="DD/MM/YYYY"
            placeholder="dd/mm/yyyy"
            onChange={(value, isValid) => {
              setFirstPoAEnd(processDate(value));
              setIsFirstPoaEndDateValid(isValid);
            }}
            minDate={firstPoAStart}
            maxDate={maxPeriodEnd}
            required={true}
          ></CalendarPicker>
        </div>
      </Panel>

      <Panel title="Irrevocable Elections" className="mt-4">
        <p>
          The following elections are irrevocable for the Group once made.
          Please select all elections that have been made by the Group in any
          period before the first period for which the model is used:
        </p>
        <div className="row">
          <p className="col-3 flex-align">
            Apply the Disregard Regulations to pre-1 April 2020 derivative
            contracts
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.applyDisregardRegulations}
            />
          </p>

          <div className="col-1 d-flex" style={{ height: 40 }}>
            <Switch
              showIndicator
              style={{ width: "fit-content" }}
              checked={disregard_regulations_election}
              onChange={(value) => {
                if (!value) {
                  set_disregard_regulations_election_year(undefined);
                  set_disregard_regulation_comment("");
                }
                set_disregard_regulations_election(value);
              }}
            />
          </div>

          <div className="col-3">
            <CalendarPicker
              fieldTitle="Date of Election"
              fieldWidth={200}
              maxDate={processDateJS(new Date(2018, 2, 31))}
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              disabled={!disregard_regulations_election}
              value={disregard_regulations_election_year}
              error={disregardRegulationsElectionError}
              useCustomValidation
              customErrorNode="Please enter a valid date"
              onChange={(value, isValid) => {
                const date = processDate(value);
                set_disregard_regulations_election_year(date);

                if (date) {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    disregard_regulations_election_date: isValid,
                  }));
                } else {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    disregard_regulations_election_date: true,
                  }));
                }
              }}
            />
          </div>
          <Input
            className="col"
            disabled={!disregard_regulations_election}
            title="Comment"
            value={disregard_regulation_comment}
            onChange={(val) => set_disregard_regulation_comment(val)}
          />
        </div>

        <div className="row">
          <p className="col-3 flex-align">
            Group EBITDA (chargeable gains) election
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.groupEBITDAChargeableGainsElection}
            />
          </p>
          <div className="col-1 d-flex" style={{ height: 40 }}>
            <Switch
              className="col-1"
              showIndicator
              style={{ width: "fit-content" }}
              checked={group_ebitda_gains}
              onChange={(value) => {
                if (!value) {
                  set_group_ebitda_gains_year(undefined);
                  set_group_ebitda_gains_comment("");
                }
                set_group_ebitda_gains(value);
              }}
            />
          </div>
          <div className="col-3">
            <CalendarPicker
              fieldTitle="End of the first PoA in relation to which election is made"
              fieldWidth={200}
              format="DD/MM/YYYY"
              disabled={!group_ebitda_gains}
              value={group_ebitda_gains_year}
              error={groupEbitdaGainsError}
              useCustomValidation
              customErrorNode="Please enter a valid date"
              placeholder="dd/mm/yyyy"
              key={firstPoAEnd}
              maxDate={maxElectionDate}
              onChange={(value, isValid) => {
                const date = processDate(value);
                set_group_ebitda_gains_year(date);

                if (date) {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    group_ebitda_gains_date: isValid,
                  }));
                } else {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    group_ebitda_gains_date: true,
                  }));
                }
              }}
            />
          </div>
          <Input
            className="col"
            disabled={!group_ebitda_gains}
            title="Comment"
            value={group_ebitda_gains_comment}
            onChange={(val) => set_group_ebitda_gains_comment(val)}
          />
        </div>

        <div className="row">
          <p className="col-3 flex-align">
            Interest allowance (alternative calculation) election
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.alternativeCalculationElection}
            />
          </p>
          <div className="col-1 d-flex" style={{ height: 40 }}>
            <Switch
              className="col-1"
              showIndicator
              style={{ width: "fit-content" }}
              checked={interest_allowance_alt_calc}
              onChange={(value) => {
                if (!value) {
                  set_interest_allowance_alt_calc_year(undefined);
                  set_interest_allowance_alt_calc_comment("");
                }
                set_interest_allowance_alt_calc(value);
              }}
            />
          </div>
          <div className="col-3">
            <CalendarPicker
              fieldTitle="End of the first PoA in relation to which election is made"
              fieldWidth={200}
              format="DD/MM/YYYY"
              disabled={!interest_allowance_alt_calc}
              value={interest_allowance_alt_calc_year}
              error={interestAllowanceAltCalcError}
              useCustomValidation
              customErrorNode="Please enter a valid date"
              placeholder="dd/mm/yyyy"
              key={firstPoAEnd}
              maxDate={maxElectionDate}
              onChange={(value, isValid) => {
                const date = processDate(value);
                set_interest_allowance_alt_calc_year(processDate(date));

                if (date) {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    interest_allowance_alt_calc_date: isValid,
                  }));
                } else {
                  setElectionsDatesValid((prevState) => ({
                    ...prevState,
                    interest_allowance_alt_calc_date: true,
                  }));
                }
              }}
            />
          </div>
          <Input
            className="col"
            disabled={!interest_allowance_alt_calc}
            title="Comment"
            value={interest_allowance_alt_calc_comment}
            onChange={(val) => set_interest_allowance_alt_calc_comment(val)}
          />
        </div>

        <div className="row">
          <p className="col-3 flex-align">
            Interest allowance (consolidated partnership) election
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.consolidatedPartnershipElection}
            />
          </p>
          <div className="col-1 d-flex" style={{ height: 40 }}>
            <Switch
              className="col-1"
              showIndicator
              style={{ width: "fit-content" }}
              checked={interest_allowance_consolidated_partnership}
              onChange={(value) => {
                if (!value) {
                  set_interest_allowance_consolidated_partnership_year(
                    undefined,
                  );
                  set_interest_allowance_consolidated_partnership_comment("");
                }
                set_interest_allowance_consolidated_partnership(value);
              }}
            />
          </div>
          <Input
            className="col"
            disabled={!interest_allowance_consolidated_partnership}
            title="Comment"
            value={interest_allowance_consolidated_partnership_comment}
            onChange={(val) =>
              set_interest_allowance_consolidated_partnership_comment(val)
            }
          />
        </div>
        {isConsolidatedPartnershipElectionTurnedOn && (
          <ConsolidatedPartnerships
            partnerships={getGroupData()?.consolidated_partnerships}
            onChange={(updatedPartnerships) => {
              const validation =
                getConsolidatedPartnershipValidation(updatedPartnerships);
              setDuplicateCPErrors(validation.duplicateErrors);
              setConsolidatedPartnershipsError(validation.hasErrors);
              setConsolidatedPartnershipsStartDateError(
                validation.hasInvalidStartDate,
              );

              // Update group data
              const currentGroupData = getGroupData();
              const newJson = {
                ...currentGroupData,
                consolidated_partnerships: updatedPartnerships,
              };
              setGroupData(newJson);
            }}
            defaultDate={interest_allowance_consolidated_partnership_year}
            isReadOnly={auth?.isReadOnlyUser()}
            showDateColumn={true}
            showDatePicker={false}
            isGroupConfig={false}
            isGroupDataPage={true}
            canEdit={(partnership) => !partnership?.electionSubmitted}
            poaPeriodEndDates={
              getGroupData()
                ?.periods_of_account?.map((poa) => poa?.period_end)
                ?.filter((date) => !!date) || []
            }
            modelFirstPeriodStartDate={getGroupData()?.model_first_period_start}
          />
        )}
      </Panel>

      <Panel
        title="Excess Debt Cap Brought Forward (Group Attribute)"
        className="mt-4"
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ display: "flex" }}>
            <p className="col">
              Excess Debt Cap Generated in period immediately before first
              period of account for which the model is used (in GBP):
            </p>
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.excessDeptCap}
            />
          </div>
          <InputNumber
            className="col"
            min={0}
            format=","
            value={excess_debt_cap_before_model}
            prefix="GBP"
            title="GBP"
            onChange={(value) => {
              set_excess_debt_cap_before_model(value);
            }}
            error={
              !Number.isInteger(excess_debt_cap_before_model) &&
              excess_debt_cap_before_model !== ""
            }
            errorNode="Excess debt cap needs to be an integer"
          />
        </div>
      </Panel>
      <Panel
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>Unused Interest Allowance (Group Attribute)</span>
            <CustomTooltipIcon
              size="big"
              tooltipText={tooltips.groupAttribute}
            />
          </div>
        }
        className="mt-4"
      >
        <p>
          Please enter any unused interest allowances generated by the group in
          all periods prior to the first period of account for which the model
          is used.
        </p>
        <table className="table">
          <thead style={{ marginBottom: "10px" }}>
            <tr>
              <CustomTableHeader
                title="Originating Period of Account Start Date"
                key={1}
                required={true}
                tooltipText={tooltips.originatingPoAStart}
              />

              <CustomTableHeader
                title="Originating Period of Account End Date"
                key={2}
                required={true}
                tooltipText={tooltips.originatingPoAEnd}
              />

              <CustomTableHeader
                title="Interest Allowance in Originating Period"
                key={3}
                required={false}
                tooltipText={tooltips.interestAllowance}
              />

              <CustomTableHeader
                title="Aggregate Net Tax-Interest Expense for Originating Period"
                key={4}
                required={false}
                tooltipText={tooltips.aggregateNetTaxInterestExpense}
              />

              <CustomTableHeader
                title="Reactivations in Originating Period"
                key={5}
                required={false}
                tooltipText={tooltips.reactivations}
              />

              <CustomTableHeader
                title="Unused Interest allowance generated in originating period"
                key={6}
                required={false}
                tooltipText={tooltips.unusedInterestAllowance}
              />

              <CustomTableHeader
                title="Amounts Utilised Prior to First Period of Account for which the model is used"
                key={7}
                required={false}
                tooltipText={tooltips.amountsUtilisedPriorFirstPoA}
              />

              <CustomTableHeader
                title="Unused Interest Allowance Potentially Available in Future Periods"
                key={8}
                required={false}
                tooltipText={tooltips.unusedInterestAllowanceFuture}
              />

              <th
                style={{ textAlign: "left", verticalAlign: "top" }}
                className="col-1"
              >
                <Button
                  add
                  disabled={!isUnusuedInterestValid()}
                  className="btn btn-outline-success"
                  title={addInterestAllowanceToolTipTitle}
                  onClick={() => {
                    if (isUnusuedInterestValid()) {
                      addTableRows();
                    }
                  }}
                >
                  <span className="Appkit4-icon icon-plus-outline"></span>
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {unusedInterest?.map((data, index) => {
              return (
                <tr key={index}>
                  <td
                    key={1}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    <CalendarPicker
                      required="true"
                      fieldWidth={calendarPickerWidth}
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                      className="col"
                      value={data["start_date"]}
                      minDate={getOriginatingPoaMinStartDateJS()}
                      maxDate={originatingPoAStartMaxDate}
                      onChange={(evnt, isValid) => {
                        tableChange(index, evnt, "start_date");
                        const validatorList = [
                          ...isUnusedInterestStartDatesValid,
                        ];
                        validatorList[index] = isValid;
                        setIsUnusedInterestStartDatesValid(validatorList);
                      }}
                    />
                  </td>
                  <td
                    key={2}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    <CalendarPicker
                      style={{
                        verticalAlign:
                          isUnusedInterestRowValid &&
                          isUnusedInterestRowValid[index]
                            ? "top"
                            : "middle",
                      }}
                      fieldWidth={calendarPickerWidth}
                      format="DD/MM/YYYY"
                      placeholder="dd/mm/yyyy"
                      className="col"
                      value={data["end_date"]}
                      minDate={originatingPoAEndDates[index]?.["minDate"]}
                      maxDate={originatingPoAEndDates[index]?.["maxDate"]}
                      required={true && data["start_date"]}
                      onChange={(evnt, isValid) => {
                        tableChange(index, evnt, "end_date");
                        const validatorList = [
                          ...isUnusedInterestEndDatesValid,
                        ];
                        validatorList[index] = isValid;
                        setIsUnusedInterestEndDatesValid(validatorList);
                      }}
                    />
                  </td>
                  <td
                    key={3}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    {getInputNumber(data, index, "interest_allowance")}
                  </td>
                  <td
                    key={4}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    {getInputNumber(data, index, "interest_expense")}
                  </td>
                  <td
                    key={5}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    {getInputNumber(
                      data,
                      index,
                      "reactivations",
                      Math.max(
                        data["interest_allowance"] - data["interest_expense"],
                        0,
                      ),
                    )}
                  </td>
                  <td
                    key={6}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    <InputNumber
                      style={{ minWidth: calendarPickerWidth - 50 }}
                      format=","
                      className="col"
                      value={data["unused"] || 0}
                      readonly
                    />
                  </td>
                  <td
                    key={7}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    <InputNumber
                      style={{ minWidth: calendarPickerWidth - 50 }}
                      format=","
                      className="col"
                      value={data["reactivations_prior_model_use"] || 0}
                      error={
                        !is_reactivations_prior_model_valid(data) ||
                        data["reactivations_prior_model_use"] < 0 ||
                        isNumberFractional(
                          data["reactivations_prior_model_use"],
                        )
                      }
                      errorNode={getInputNumberErrorMessage(
                        "reactivations_prior_model_use",
                        data["reactivations_prior_model_use"],
                        undefined,
                      )}
                      max={Math.max(
                        data["interest_allowance"] -
                          data["interest_expense"] -
                          data["reactivations"],
                        0,
                      )}
                      onChange={(evnt) => {
                        tableChange(
                          index,
                          evnt,
                          "reactivations_prior_model_use",
                        );
                      }}
                    />
                  </td>
                  <td
                    key={8}
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                    }}
                  >
                    <InputNumber
                      style={{ minWidth: calendarPickerWidth - 50 }}
                      format=","
                      className="col"
                      value={data["unused_available"] || 0}
                      readonly
                    />
                  </td>
                  <td
                    style={{
                      verticalAlign:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? "middle"
                          : "top",
                      paddingTop:
                        isUnusedInterestRowValid &&
                        isUnusedInterestRowValid[index]
                          ? ""
                          : "0.35rem",
                    }}
                  >
                    <Button
                      className="btn btn-outline-danger"
                      onClick={(e) => deleteTableRows(e, index)}
                    >
                      <span className="Appkit4-icon icon-close-outline"></span>
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="row">
          <div className="col">
            <Button
              style={{ float: "left" }}
              disabled={!isUnusuedInterestValid()}
              title={addInterestAllowanceToolTipTitle}
              onClick={() => {
                if (isUnusuedInterestValid()) {
                  addTableRows();
                }
              }}
            >
              Add new period
            </Button>
          </div>
        </div>
      </Panel>

      <div className="container-fluid mt-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <BackButton disabled={isPosting} />
          </div>
          <div className="d-flex gap-2">
            <Button
              hiddenInReadonly
              loading={isPosting}
              disabled={isSaveDisabled}
              onClick={save}
              kind="secondary"
            >
              Save
            </Button>
            <NextButton
              preNavigation={save}
              loading={isPosting}
              disabled={isSaveDisabled}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupConfig;
